/**
 * GuestLimitModal - Modal shown to guest users when they try to access
 * features that require an account (rewards, collection, heroes, world, transformation)
 */

import React from 'react';
import { Trophy, Sparkles, Cloud, Gift } from 'lucide-react';
import { useAppData } from '../../hooks/useAppData';
import { Capacitor } from '@capacitor/core';

interface GuestLimitModalProps {
  onClose: () => void;
  onCreateAccount: () => void;
  onLogin: () => void;
  feature: 'rewards' | 'collection' | 'heroes' | 'world' | 'transformation';
}

const GuestLimitModal: React.FC<GuestLimitModalProps> = ({
  onClose,
  onCreateAccount,
  onLogin,
  feature,
}) => {
  const { modalTexts } = useAppData();
  const content = modalTexts.guestLimit[feature];
  const isNative = Capacitor.isNativePlatform();

  // Avantages marketing avec icônes
  const benefits = [
    {
      icon: Trophy,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      title: 'Collectionne tout',
      description: 'Découvre et collectionne des objets uniques de l\'univers LilHero'
    },
    {
      icon: Sparkles,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      title: 'Personnalise ton héros',
      description: 'Crée ton monde unique avec ton héros et tes objets préférés'
    },
    {
      icon: Cloud,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      title: 'Synchronisé partout',
      description: 'Retrouve ta progression sur tous tes appareils, à tout moment'
    },
    {
      icon: Gift,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-50',
      title: '100% Gratuit',
      description: 'Crée ton compte en quelques secondes, sans carte bancaire'
    }
  ];

  return (
    <div className={`fixed inset-0 z-50 ${isNative ? 'bg-white' : 'bg-black bg-opacity-50 flex items-center justify-center p-4'}`}>
      <div className={`bg-white relative ${
        isNative
          ? 'w-full h-full overflow-y-auto safe-area-bottom'
          : 'rounded-2xl shadow-2xl max-w-md w-full overflow-hidden'
      }`}>

        {/* Header */}
        <div className={`bg-gradient-to-r from-purple-500 to-pink-500 text-white ${
          isNative ? 'p-6 pt-16 safe-area-top' : 'p-6'
        }`}>
          <div className="text-center">
            <div className={`mb-2 ${isNative ? 'text-4xl' : 'text-5xl'}`}>🎁</div>
            <h2 className={`font-bold ${isNative ? 'text-xl' : 'text-2xl'}`}>
              Crée ton compte gratuit
            </h2>
            <p className="text-white text-opacity-90 text-sm mt-1">
              Pour profiter de toutes les fonctionnalités
            </p>
          </div>
        </div>

        {/* Content */}
        <div className={isNative ? 'p-4' : 'p-6'}>
          {/* Benefits blocks - like SubscriptionModal */}
          <div className="space-y-2 mb-5">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 ${benefit.bgColor} rounded-lg`}
                >
                  <div className={`w-9 h-9 rounded-full ${benefit.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="text-white" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm">{benefit.title}</h3>
                    <p className="text-xs text-gray-600 line-clamp-2">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Call to Action */}
          <div className="space-y-2">
            <button
              onClick={onCreateAccount}
              className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-md flex items-center justify-center gap-2"
            >
              <Sparkles size={18} />
              {modalTexts.guestLimit.buttons.createAccount}
            </button>
            <button
              onClick={onLogin}
              className="w-full py-2.5 bg-white text-gray-700 rounded-lg font-medium border border-gray-300 hover:border-gray-400 transition-colors"
            >
              {modalTexts.guestLimit.buttons.login}
            </button>
            <button
              onClick={onClose}
              className="w-full py-2 text-gray-400 rounded-lg text-sm hover:text-gray-600 transition-colors"
            >
              {modalTexts.guestLimit.buttons.later}
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-4">
            {modalTexts.guestLimit.footer}
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestLimitModal;
